// R2 (Feb 2026) - Account setup UI rewrite.
//
// This page is the Workspace Admin's Account setup experience. It uses
// the R1 backend contract:
//   GET /api/onboarding/checklist -> { steps: [...], percent, account_setup_complete }
//
// R2 scope (approved):
//   - 10 Account setup steps in the approved order
//   - Header + step copy per approved wording (no em / en dashes)
//   - Step 1 mandatory guard (business type + at least one country)
//   - Full country list from /api/countries via useCountries()
//   - Locations as its own optional step (never attached to Teams)
//   - Shift templates as its own optional step
//   - Teams step does NOT ask for Location; Parent team lives under Advanced
//   - Working patterns step offers simple presets inside setup and
//     completes only after an explicit manager choice (pattern saved
//     or "Flexible or no fixed pattern")
//   - Employees step explains access levels
//   - First roster step uses R1 account_setup_complete / first_roster
//   - Simple hand-off card visible once account_setup_complete is true
//
// R3 (Rota Style setup page) is NOT started in this pass.
//
// Feb 2026 review-and-confirm cleanup:
//   - Step 1 (Company) and Step 2 (Work categories) are no longer marked
//     complete just because data exists. The manager must click Continue
//     inside Account setup for these two steps. Reviewed state is stored
//     per company in localStorage under `nextrota.setup.reviewed.<id>`.
//     Nothing else about backend completion changed.
//   - The presentational "premium NextRota areas" section was removed
//     from Account setup so the page focuses on basic setup only.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCountries } from "../lib/countries";
import { hasGrowthAccess } from "../lib/effectivePlan";
import { buildScheduledCycleWeeks, todayIsoMonday } from "./WorkingPatternsPage";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft, ArrowRight, Building2, Briefcase, Lightbulb,
  MapPin, Users, UserPlus, Calendar as CalendarIcon, Clock,
  Sparkles, ShieldCheck, ChevronRight, ChevronDown, Plus, Check,
  X, Loader2, AlertCircle, Layers,
} from "lucide-react";


// ---------- Static reference data ----------------------------------

// Mirrors backend `COMPANY_TYPES`.
const COMPANY_TYPES = [
  { key: "customer_support",     label: "Customer support",      hint: "Contact centres, helpdesks, BPO",        template: "contact_centre_small" },
  { key: "office_professional",  label: "Office or professional", hint: "Knowledge workers, consultancies",      template: "office_small" },
  { key: "retail",               label: "Retail",                hint: "Shops, e-commerce fulfilment",           template: "retail_small" },
  { key: "hospitality",          label: "Hospitality",           hint: "Restaurants, hotels, events",            template: null },
  { key: "logistics_transport",  label: "Logistics or transport", hint: "Warehouses, fleet, delivery",           template: "warehouse_small" },
  { key: "manufacturing_warehouse", label: "Manufacturing or warehouse", hint: "Production lines, fulfilment",   template: "warehouse_small" },
  { key: "healthcare",           label: "Healthcare",            hint: "Clinics, care, hospitals",               template: "healthcare_small" },
  { key: "education",            label: "Education",             hint: "Schools, training providers",            template: null },
  { key: "field_services",       label: "Field services",        hint: "Engineers, technicians, on-site",        template: null },
  { key: "other",                label: "Other",                 hint: "Pick this if nothing else fits",         template: null },
];


// Popular country codes pinned at the top of the picker for one-click
// selection. Everything else comes from the full ISO list returned by
// /api/countries via useCountries().
const POPULAR_COUNTRY_CODES = [
  "GB", "IE", "US", "CA", "AU", "NZ", "IN",
  "DE", "FR", "ES", "NL", "SE", "ZA", "SG",
];


// Step metadata for the new 10-step Account setup flow. Order is
// mandated by the R2 approval message. `key` maps to the R1 backend
// checklist key that answers "is this step done?" for the completion
// map computed below.
const STEP_META = [
  { n: 1,  short: "Company",         icon: Building2,   title: "Tell us about your company",
    why: "This helps NextRota suggest the right setup for your business and prepare the correct public holiday list." },
  { n: 2,  short: "Work categories", icon: Briefcase,   title: "Add your work categories",
    why: "Work categories help you assign the right work in the roster and keep reporting accurate." },
  { n: 3,  short: "Skills",          icon: Lightbulb,   title: "Set up employee skills",
    why: "Skills help managers assign the right people to the right work." },
  { n: 4,  short: "Bank holidays",   icon: CalendarIcon, title: "Set up bank holidays",
    why: "Bank holidays can affect leave, staffing levels and roster planning." },
  { n: 5,  short: "Locations",       icon: MapPin,      title: "Add work locations",
    why: "Locations help employees know where they are working. They also help managers plan shifts across different sites." },
  { n: 6,  short: "Shift templates", icon: Layers,      title: "Review your shift templates",
    why: "Templates save managers time because they do not need to rebuild common work and break patterns every time." },
  { n: 7,  short: "Teams",           icon: Users,       title: "Create your teams",
    why: "Teams help managers organise employees, control access and build rosters for the right group of people." },
  { n: 8,  short: "Working patterns", icon: Clock,      title: "Set working patterns",
    why: "Working patterns help managers understand normal availability before building rosters." },
  { n: 9,  short: "Employees",       icon: UserPlus,    title: "Add employees and choose access",
    why: "Access levels keep the right people in control while employees see only what they need." },
  { n: 10, short: "First roster",    icon: Sparkles,    title: "Create your first roster",
    why: "Your first roster confirms that your teams, employees, categories and templates are working together." },
];


// ---------- Page ---------------------------------------------------

// Feb 2026 review-and-confirm helper. Read + write reviewed state for
// Step 1 (Company) and Step 2 (Work categories). Stored in localStorage
// only; no new backend field, no new endpoint.
const REVIEWED_STORAGE_PREFIX = "nextrota.setup.reviewed.";
function readReviewed(companyId) {
  if (!companyId) return {};
  try {
    const raw = localStorage.getItem(`${REVIEWED_STORAGE_PREFIX}${companyId}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === "object") ? parsed : {};
  } catch { return {}; }
}
function writeReviewed(companyId, next) {
  if (!companyId) return;
  try {
    localStorage.setItem(`${REVIEWED_STORAGE_PREFIX}${companyId}`,
                         JSON.stringify(next || {}));
  } catch { /* ignore quota */ }
}

export default function SetupAssistantPage() {
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [company, setCompany] = useState(null);
  const [billingMe, setBillingMe] = useState(null);
  const [shiftTemplatesCount, setShiftTemplatesCount] = useState(0);
  const [bankHolidaysMissingCountries, setBankHolidaysMissingCountries] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [resumed, setResumed] = useState(false);
  const [reviewed, setReviewed] = useState({});

  const load = useCallback(async () => {
    try {
      // /companies returns this user's own company as a single-item list.
      // /billing/me is used ONLY to show the correct hand-off card at the
      // end. It never gates any step.
      const [cl, cos, bm, tpl] = await Promise.all([
        api.get("/onboarding/checklist"),
        api.get("/companies"),
        api.get("/billing/me").catch(() => ({ data: null })),
        api.get("/shift-templates").catch(() => ({ data: [] })),
      ]);
      setChecklist(cl.data);
      const co = (cos.data && cos.data[0]) || null;
      setCompany(co);
      setBillingMe(bm.data || null);
      setShiftTemplatesCount((tpl.data || []).length);
      if (co?.company_id) setReviewed(readReviewed(co.company_id));
      // Feb 2026 -- track which assigned countries still have no
      // active bank holidays for the current year. Used both by
      // the Step 4 warning banner and by the wizard completion map
      // so the step does not tick complete until every country is
      // covered. One fetch per country, results cached until the
      // next `load()`. Best-effort: on any error we treat the
      // country as covered so we never falsely block completion.
      try {
        const yr = new Date().getFullYear();
        const cList = (co?.countries || []);
        if (cList.length > 0) {
          const results = await Promise.all(cList.map(async (cc) => {
            try {
              const r = await api.get("/bank-holidays",
                                        { params: { country_code: cc, year: yr } });
              const hasActive = (r.data || []).some((h) => h.is_active !== false);
              return { cc, hasActive };
            } catch { return { cc, hasActive: true }; }
          }));
          setBankHolidaysMissingCountries(
            results.filter((x) => !x.hasActive).map((x) => x.cc));
        } else {
          setBankHolidaysMissingCountries([]);
        }
      } catch { /* non-fatal */ }
    } catch (err) {
      toast.error("Could not load setup state");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function markReviewed(stepNumber) {
    if (!company?.company_id) return;
    const next = { ...readReviewed(company.company_id), [stepNumber]: true };
    writeReviewed(company.company_id, next);
    setReviewed(next);
  }

  // Per-step completion is derived from the R1 backend checklist for
  // most steps. Step 1 (Company) and Step 2 (Work categories) require
  // an explicit Continue click, tracked in localStorage per company.
  // Step 8 (Working patterns) completes only after an explicit choice
  // inside the step (a saved pattern + Continue, or the "Flexible or
  // no fixed pattern" option + Continue); the choice is recorded in
  // the same reviewed store. Optional steps still show completion
  // when their key is done, but they never block Account setup
  // completion (that still comes from the R1 boolean, unchanged).
  const completion = useMemo(() => {
    const byKey = {};
    for (const s of (checklist?.steps || [])) byKey[s.key] = s.done;
    const step1Data = !!(company?.company_type) && !!(company?.countries?.length);
    // Feb 2026 -- Step 4 requires holidays for every assigned
    // country (or a manager who explicitly disabled the step; that
    // path already zeros the count and satisfies the check).
    const bankHolidaysCovered = !!byKey.bank_holidays
                                  && bankHolidaysMissingCountries.length === 0;
    return {
      1: step1Data && !!reviewed[1],
      2: !!byKey.categories && !!reviewed[2],
      3: !!byKey.skills,
      4: bankHolidaysCovered,
      5: !!byKey.locations,
      6: shiftTemplatesCount > 0, // client-side derivation (no new backend field)
      7: !!byKey.team,
      8: !!reviewed[8],
      9: !!byKey.employee,
      10: !!byKey.first_roster,
    };
  }, [checklist, company, shiftTemplatesCount, reviewed, bankHolidaysMissingCountries]);

  const totalDone = useMemo(
    () => Object.values(completion).filter(Boolean).length,
    [completion],
  );
  const pct = Math.round((totalDone / 10) * 100);
  const accountSetupComplete = !!checklist?.account_setup_complete;

  // Open the first incomplete step once, after the initial checklist
  // load. Because Step 1 and Step 2 now require a Continue click, a
  // fresh customer whose registration pre-filled company_type + country
  // will still open at Step 1 first. Re-opens at Step 10 (summary)
  // when everything is done.
  useEffect(() => {
    if (resumed || !checklist || !company) return;
    let firstIncomplete = 0;
    for (let n = 1; n <= 10; n++) {
      if (!completion[n]) { firstIncomplete = n; break; }
    }
    setCurrentStep(firstIncomplete || 10);
    setResumed(true);
  }, [checklist, company, completion, resumed]);

  function goToStep(n) { setCurrentStep(n); }
  function goNext()    { setCurrentStep((s) => Math.min(10, s + 1)); }
  function goBack()    { setCurrentStep((s) => Math.max(1, s - 1)); }

  const sectionProps = { company, completion, load, goNext, goBack, navigate, markReviewed };

  return (
    <div className="min-h-full bg-[#F8FAFD]" data-testid="setup-assistant-page">
      <div className="max-w-[1320px] mx-auto px-6 py-7">

        {/* Header - approved copy */}
        <header className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 mb-6">
          <div className="bg-gradient-to-br from-[#F5F8FE] to-[#EFF3FB] rounded-xl px-7 py-7 border border-[#E4EAF4]">
            <p className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold mb-3"
               data-testid="setup-eyebrow">
              Account setup
            </p>
            <h1 className="text-[28px] font-semibold text-[#0B1A36] leading-tight"
                data-testid="setup-hero-title">
              Set up your NextRota account
            </h1>
            <p className="text-[13px] text-[#4B5773] mt-2 max-w-[640px]"
               data-testid="setup-hero-subtitle">
              Complete the key setup steps so you can build your first roster.
              You can edit these settings later.
            </p>
          </div>
          <Card className="px-5 py-5 shadow-sm border-[#E4EAF4]" data-testid="setup-progress-card">
            <p className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
              Your setup progress
            </p>
            <p className="text-[32px] font-semibold text-[#0B1A36] leading-none mt-2">
              {pct}%
            </p>
            <div className="h-2 bg-[#E4EAF4] rounded-full mt-3 overflow-hidden">
              <div className="h-full bg-emerald-500 transition-[width] duration-500"
                   style={{ width: `${pct}%` }} />
            </div>
            <p className="text-[12px] text-[#4B5773] mt-2">
              {totalDone} of 10 steps complete
            </p>
          </Card>
        </header>

        {/* Stepper - simple numbered circles */}
        <Stepper currentStep={currentStep} completion={completion}
                 onSelect={goToStep} />

        {/* Main column + side column */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 mt-6">
          <div data-testid="setup-main-card">
            {currentStep === 1 && <Step1Company {...sectionProps} />}
            {currentStep === 2 && <Step2Categories {...sectionProps} />}
            {currentStep === 3 && <Step3Skills {...sectionProps} />}
            {currentStep === 4 && <Step4Holidays {...sectionProps} />}
            {currentStep === 5 && <Step5Locations {...sectionProps} />}
            {currentStep === 6 && <Step6ShiftTemplates {...sectionProps}
                                                        templatesCount={shiftTemplatesCount} />}
            {currentStep === 7 && <Step7Teams {...sectionProps} />}
            {currentStep === 8 && <Step8Patterns {...sectionProps} />}
            {currentStep === 9 && <Step9Employees {...sectionProps} />}
            {currentStep === 10 && <Step10FirstRoster {...sectionProps}
                                                      accountSetupComplete={accountSetupComplete}
                                                      billingMe={billingMe} />}
          </div>

          <aside className="space-y-4" data-testid="setup-side-column">
            <SideCardWhy step={STEP_META[currentStep - 1]} />
            <SideCardWhatsNext currentStep={currentStep} completion={completion}
                                 onSelect={goToStep} />
          </aside>
        </div>

        {/* Footer - account at a glance */}
        <section className="mt-8">
          <h2 className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold mb-3">
            Your account at a glance
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
               data-testid="setup-glance">
            <GlanceCard icon={UserPlus} value={completion[9] ? "Yes" : "Add"}
                          label="Employees added"
                          hint={completion[9] ? "You have at least one employee." : "Head to Step 9."} />
            <GlanceCard icon={Users} value={completion[7] ? "Yes" : "Add"}
                          label="Teams created"
                          hint={completion[7] ? "You have at least one team." : "Head to Step 7."} />
            <GlanceCard icon={CalendarIcon} value={completion[10] ? "Yes" : "Add"}
                          label="First roster started"
                          hint={completion[10]
                            ? "A shift exists for this week or next week."
                            : "Add a shift for this week or next week."} />
            <GlanceCard icon={ShieldCheck} value={`${pct}%`}
                          label="Setup progress"
                          hint={`${totalDone} of 10 steps complete`}
                          showBar progressPct={pct} />
          </div>
        </section>

        <div className="mt-6 px-5 py-4 rounded-xl bg-[#F1F5FB] border border-[#E4EAF4] flex items-center gap-3"
             data-testid="setup-security-strip">
          <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />
          <div className="flex-1">
            <p className="text-[13px] font-medium text-[#0B1A36]">Your data is secure</p>
            <p className="text-[11px] text-[#4B5773]">We use enterprise-grade security to keep your data safe and compliant.</p>
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------- Stepper ------------------------------------------------

function Stepper({ currentStep, completion, onSelect }) {
  const doneCount = Object.values(completion).filter(Boolean).length;
  const linePct = ((Math.max(0, doneCount - 1)) / 9) * 100;
  const currentMeta = STEP_META.find((s) => s.n === currentStep) || STEP_META[0];
  const totalSteps = STEP_META.length;
  const pctDone = Math.round((doneCount / totalSteps) * 100);
  return (
    <div className="mt-2" data-testid="setup-stepper">
      {/* Mobile compact bar */}
      <div className="block sm:hidden px-1" data-testid="setup-stepper-mobile">
        <div className="flex items-baseline justify-between gap-2 mb-2">
          <div className="text-xs uppercase tracking-widest text-[#4B5773] font-semibold">
            Step {currentStep} of {totalSteps}
          </div>
          <div className="text-xs text-[#4B5773]" data-testid="setup-stepper-mobile-pct">
            {pctDone}% complete
          </div>
        </div>
        <div className="text-sm font-medium text-[#0B1A36] mb-2 truncate">
          {currentMeta.short}
        </div>
        <div className="h-1.5 rounded-full bg-[#E4EAF4] overflow-hidden">
          <div className="h-full bg-emerald-500 transition-[width] duration-500"
               style={{ width: `${pctDone}%` }} />
        </div>
      </div>
      {/* Desktop 10-circle grid */}
      <div className="relative px-5 hidden sm:block">
        <div className="absolute top-[18px] left-[34px] right-[34px] h-[2px] bg-[#E4EAF4] rounded-full" />
        <div className="absolute top-[18px] left-[34px] h-[2px] bg-emerald-500 rounded-full transition-[width] duration-500"
             style={{ width: `calc((100% - 68px) * ${linePct / 100})` }} />
        <div className="grid grid-cols-10 gap-2 relative">
          {STEP_META.map((s) => {
            const isCurrent = s.n === currentStep;
            const isDone = !!completion[s.n];
            return (
              <button
                key={s.n}
                type="button"
                onClick={() => onSelect(s.n)}
                data-testid={`setup-step-circle-${s.n}`}
                data-state={isDone ? "done" : isCurrent ? "current" : "future"}
                className="flex flex-col items-center group cursor-pointer focus:outline-none"
              >
                <span
                  className={[
                    "w-9 h-9 rounded-full flex items-center justify-center",
                    "text-[12px] font-semibold border-2 transition-colors z-10",
                    // Feb 2026 -- do NOT include bg-white in the base
                    // class; it collides with bg-emerald-500 on the
                    // completed state and can win in the generated CSS
                    // order, leaving a white circle with a green
                    // border and an invisible white checkmark. Each
                    // branch owns its full background.
                    isDone
                      ? "bg-emerald-500 border-emerald-500 text-white shadow-sm"
                      : isCurrent
                        ? "bg-[#2563EB] border-[#2563EB] text-white"
                        : "bg-white border-[#E4EAF4] text-[#94A3B8]",
                  ].join(" ")}
                >
                  {isDone
                    ? <Check className="h-5 w-5" strokeWidth={3} aria-hidden />
                    : s.n}
                </span>
                <span
                  className={[
                    "mt-2 text-[11px] text-center leading-tight",
                    isCurrent ? "text-[#0B1A36] font-medium" : "text-[#4B5773]",
                  ].join(" ")}
                >
                  {s.short}
                </span>
                {isDone && (
                  <span className="mt-1 text-[9px] uppercase tracking-widest font-semibold text-emerald-700"
                        data-testid={`setup-step-complete-chip-${s.n}`}>
                    Complete
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ---------- Step 1 - Company (MANDATORY, with hard guard) ---------

function Step1Company({ company, completion, load, goNext, markReviewed }) {
  const [companyType, setCompanyType] = useState(company?.company_type || "");
  const [countries, setCountries] = useState(company?.countries || []);
  const [saving, setSaving] = useState(false);
  const allCountries = useCountries();
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [countryFilter, setCountryFilter] = useState("");

  useEffect(() => {
    setCompanyType(company?.company_type || "");
    setCountries(company?.countries || []);
  }, [company]);

  function toggleCountry(code) {
    setCountries((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  }

  const popular = useMemo(() => {
    // Pin popular countries at the top; if useCountries has not
    // resolved yet, fall back to a small local list so the picker
    // still renders something during the first paint.
    if (!allCountries.length) {
      return POPULAR_COUNTRY_CODES.map((c) => ({ code: c, name: c }));
    }
    const byCode = Object.fromEntries(allCountries.map((c) => [c.code, c]));
    return POPULAR_COUNTRY_CODES
      .map((c) => byCode[c])
      .filter(Boolean);
  }, [allCountries]);

  const restFiltered = useMemo(() => {
    if (!allCountries.length) return [];
    const pinned = new Set(POPULAR_COUNTRY_CODES);
    const q = countryFilter.trim().toLowerCase();
    return allCountries.filter((c) => {
      if (pinned.has(c.code)) return false;
      if (!q) return true;
      return c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
    });
  }, [allCountries, countryFilter]);

  // Mandatory guard for the Continue button.
  const canContinue = !!companyType && countries.length > 0;

  async function save() {
    if (!canContinue) {
      toast.error("Choose a business type and at least one country.");
      return;
    }
    setSaving(true);
    try {
      const prevType = company?.company_type || "";
      const typeChanged = companyType !== prevType;

      await api.patch(`/companies/${company.company_id}`,
                       { company_type: companyType, countries });

      const tmplKey = COMPANY_TYPES.find((t) => t.key === companyType)?.template;
      if (tmplKey && typeChanged) {
        try {
          const existing = await api.get("/categories");
          const existingNames = new Set(
            (existing.data || [])
              .filter((c) => c.kind === "work")
              .map((c) => (c.name || "").trim().toLowerCase()),
          );
          const r = await api.get(`/setup/templates/${tmplKey}`);
          const suggestions = r.data?.categories || [];
          for (const s of suggestions) {
            const nm = (s.name || "").trim().toLowerCase();
            if (!nm || existingNames.has(nm)) continue;
            try {
              const { data: created } = await api.post("/categories", {
                name: s.name,
                code: s.code || s.name.toUpperCase().slice(0, 4),
                kind: "work", color: s.colour || "#2563EB",
              });
              try {
                await api.post("/skills", {
                  name: s.name, kind: "assignment",
                  source_category_id: created.category_id || created.id,
                });
              } catch { /* Step 3 can add manually */ }
            } catch { /* duplicate quietly skipped */ }
          }
        } catch { /* template fetch failed - non-fatal */ }
      }
      toast.success("Company details saved.");
      if (typeof markReviewed === "function") markReviewed(1);
      await load();
      goNext();
    } catch (err) {
      toast.error("Could not save company details");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="px-7 py-6 shadow-sm border-[#E4EAF4]" data-testid="setup-step-1-company">
      <StepHeader n={1} done={completion[1]} icon={Building2}
                  title="Tell us about your company"
                  subtitle="Choose your business type and the countries you operate in. NextRota uses this to suggest work categories and prepare public holidays." />
      <div className="mt-5 space-y-5">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
            Business type
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2"
               data-testid="setup-company-type-grid">
            {COMPANY_TYPES.map((t) => {
              const active = companyType === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setCompanyType(t.key)}
                  data-testid={`setup-company-type-${t.key}`}
                  className={[
                    "text-left rounded-lg border px-3.5 py-2.5 transition-colors",
                    active
                      ? "border-[#2563EB] bg-[#EFF3FB]"
                      : "border-[#E4EAF4] bg-white hover:border-[#2563EB]/40 hover:bg-[#EFF3FB]/50",
                  ].join(" ")}
                >
                  <div className="text-[13px] font-medium text-[#0B1A36]">
                    {t.label}
                  </div>
                  <div className="text-[11px] text-[#4B5773] mt-0.5">{t.hint}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
            Country or countries
          </label>
          <p className="text-[11px] text-[#4B5773] mt-1">
            Pick every country your team operates in. Popular options
            appear first. You can search the full list below.
          </p>

          <div className="flex flex-wrap gap-1.5 mt-2"
               data-testid="setup-countries">
            {popular.map((c) => {
              const on = countries.includes(c.code);
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => toggleCountry(c.code)}
                  data-testid={`setup-country-${c.code}`}
                  className={[
                    "text-[12px] px-2.5 py-1.5 rounded-full border transition-colors",
                    on
                      ? "border-[#2563EB] bg-[#EFF3FB] text-[#0B1A36]"
                      : "border-[#E4EAF4] bg-white text-[#4B5773] hover:border-[#2563EB]/40",
                  ].join(" ")}
                >
                  {on && <Check className="inline h-3 w-3 mr-1" />}
                  {c.name}
                </button>
              );
            })}
          </div>

          <div className="mt-3">
            <button type="button"
                    onClick={() => setShowAllCountries((v) => !v)}
                    className="text-[12px] text-[#2563EB] hover:underline inline-flex items-center gap-1"
                    data-testid="setup-country-toggle-all">
              {showAllCountries ? "Hide full country list" : "Show all countries"}
              <ChevronDown className={`h-3 w-3 transition-transform ${showAllCountries ? "rotate-180" : ""}`} />
            </button>
          </div>

          {showAllCountries && (
            <div className="mt-2 border border-[#E4EAF4] rounded-lg bg-white"
                 data-testid="setup-country-full-list">
              <div className="px-2 py-2 border-b border-[#F1F5FB]">
                <Input
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  placeholder="Search countries..."
                  className="text-sm"
                  data-testid="setup-country-search"
                />
              </div>
              <div className="max-h-64 overflow-y-auto">
                {restFiltered.length === 0 ? (
                  <div className="text-[12px] text-[#94A3B8] px-3 py-4 text-center">
                    No matches. Popular countries are already listed above.
                  </div>
                ) : (
                  restFiltered.map((c) => {
                    const on = countries.includes(c.code);
                    return (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => toggleCountry(c.code)}
                        data-testid={`setup-country-full-${c.code}`}
                        className={[
                          "w-full flex items-center gap-2 px-3 py-2 text-left text-[12px] border-b border-[#F1F5FB] last:border-b-0",
                          on ? "bg-[#EFF3FB]" : "hover:bg-[#F8FAFD]",
                        ].join(" ")}
                      >
                        <span className={`w-4 h-4 rounded border ${on ? "bg-[#2563EB] border-[#2563EB]" : "border-[#E4EAF4]"} grid place-items-center`}>
                          {on && <Check className="h-3 w-3 text-white" />}
                        </span>
                        <span className="text-[#0B1A36]">{c.name}</span>
                        <span className="ml-auto text-[10px] uppercase tracking-widest text-[#94A3B8]">{c.code}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {!canContinue && (
          <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2"
               data-testid="setup-step-1-guard-hint">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>Choose a business type and at least one country to continue.</span>
          </div>
        )}
      </div>
      <StepFooter onBack={null}
                  onNext={save}
                  nextLabel="Save and continue"
                  nextDisabled={!canContinue}
                  saving={saving}
                  testIdPrefix="setup-step-1" />
    </Card>
  );
}


// ---------- Step 2 - Work categories ------------------------------

function Step2Categories({ company, completion, load, goNext, goBack, markReviewed }) {
  const [suggested, setSuggested] = useState([]);
  const [existing, setExisting] = useState([]);
  const [customName, setCustomName] = useState("");
  const [saving, setSaving] = useState(false);

  const loadRows = useCallback(async () => {
    try {
      const [cats] = await Promise.all([api.get("/categories")]);
      setExisting((cats.data || []).filter(
        (c) => c.kind === "work" && c.is_active !== false,
      ));
      const tmplKey = COMPANY_TYPES.find((t) => t.key === company?.company_type)?.template;
      if (tmplKey) {
        try {
          const r = await api.get(`/setup/templates/${tmplKey}`);
          setSuggested((r.data?.categories || []).map((c) => ({
            name: c.name, code: c.code, color: c.colour || "#2563EB",
          })));
        } catch { setSuggested([]); }
      } else {
        setSuggested([
          { name: "Calls",    code: "CALL", color: "#2563EB" },
          { name: "Emails",   code: "EMAL", color: "#0EA5E9" },
          { name: "Admin",    code: "ADMN", color: "#10B981" },
          { name: "Training", code: "TRN",  color: "#F59E0B" },
        ]);
      }
    } catch {
      toast.error("Could not load categories");
    }
  }, [company]);

  useEffect(() => { loadRows(); }, [loadRows]);

  const existingNames = useMemo(
    () => new Set(existing.map((c) => (c.name || "").toLowerCase())),
    [existing],
  );

  async function addCategory({ name, code, color }) {
    setSaving(true);
    try {
      const { data: created } = await api.post("/categories", {
        name, code: code || name.toUpperCase().slice(0, 4),
        kind: "work", color: color || "#2563EB",
      });
      try {
        await api.post("/skills", {
          name, kind: "assignment",
          source_category_id: created.category_id || created.id,
        });
      } catch { /* Step 3 handles */ }
      toast.success(`Added "${name}".`);
      await Promise.all([loadRows(), load()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add category");
    } finally {
      setSaving(false);
    }
  }

  async function removeCategory(id) {
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Removed.");
      await Promise.all([loadRows(), load()]);
    } catch (err) {
      const msg = err.response?.data?.detail || "";
      if (err.response?.status === 409 || /deactivate/i.test(msg)) {
        try {
          await api.patch(`/categories/${id}`, { is_active: false });
          toast.success("Removed from your active categories.");
          await Promise.all([loadRows(), load()]);
          return;
        } catch (e2) {
          toast.error(e2.response?.data?.detail || "Could not remove");
          return;
        }
      }
      toast.error(msg || "Could not remove");
    }
  }

  async function addCustom() {
    const name = customName.trim();
    if (!name) return;
    await addCategory({ name });
    setCustomName("");
  }

  const unaddedSuggestions = suggested.filter(
    (s) => !existingNames.has((s.name || "").toLowerCase()),
  );

  return (
    <Card className="px-7 py-6 shadow-sm border-[#E4EAF4]" data-testid="setup-step-2-categories">
      <StepHeader n={2} done={completion[2]} icon={Briefcase}
                  title="Add your work categories"
                  subtitle="Work categories are the types of work your employees do, such as Reception, Kitchen, Admin or Delivery. We suggested a starter list based on your company type. You can edit it, remove what you do not need, or add your own." />
      <div className="mt-5 space-y-5">
        {unaddedSuggestions.length > 0 && (
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
              Suggested for your sector
            </label>
            <div className="flex flex-wrap gap-1.5 mt-2"
                 data-testid="setup-category-suggestions">
              {unaddedSuggestions.map((s) => (
                <button
                  key={s.name}
                  type="button"
                  onClick={() => addCategory(s)}
                  disabled={saving}
                  data-testid={`setup-suggest-category-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-[12px] px-2.5 py-1.5 rounded-full border border-[#E4EAF4] bg-white text-[#0B1A36] hover:border-[#2563EB]/40 hover:bg-[#EFF3FB]/50 inline-flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" />
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
            Add your own
          </label>
          <div className="flex gap-2 mt-2">
            <Input
              placeholder="e.g. Reception, Warehouse, Cleaning"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCustom()}
              className="text-sm"
              data-testid="setup-category-custom-input"
            />
            <Button onClick={addCustom} disabled={!customName.trim() || saving}
                     data-testid="setup-category-custom-add">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
            Your categories ({existing.length})
          </label>
          <div className="mt-2 border border-[#E4EAF4] rounded-lg overflow-hidden">
            {existing.length === 0 ? (
              <div className="text-[12px] text-[#94A3B8] px-3 py-4 text-center"
                   data-testid="setup-category-empty">
                No categories yet. Pick suggestions above or add your own.
              </div>
            ) : (
              existing.map((c) => (
                <div key={c.category_id}
                     className="flex items-center justify-between px-3 py-2 border-b border-[#F1F5FB] last:border-b-0"
                     data-testid={`setup-category-row-${c.category_id}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full"
                          style={{ background: c.color || "#2563EB" }} />
                    <span className="text-[13px] text-[#0B1A36]">{c.name}</span>
                  </div>
                  <button type="button" onClick={() => removeCategory(c.category_id)}
                          className="text-[#94A3B8] hover:text-rose-600"
                          data-testid={`setup-category-remove-${c.category_id}`}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <StepFooter onBack={goBack}
                   onNext={() => {
                     if (typeof markReviewed === "function") markReviewed(2);
                     goNext();
                   }}
                   nextLabel={existing.length > 0 ? "Continue" : "Skip for now"}
                   testIdPrefix="setup-step-2" />
    </Card>
  );
}


// ---------- Step 3 - Employee skills ------------------------------

function Step3Skills({ completion, load, goNext, goBack }) {
  const [skills, setSkills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [extraName, setExtraName] = useState("");
  const [saving, setSaving] = useState(false);
  const [linkingCatId, setLinkingCatId] = useState(null);

  const loadRows = useCallback(async () => {
    try {
      const [sk, cat] = await Promise.all([
        api.get("/skills"),
        api.get("/categories"),
      ]);
      setSkills(sk.data || []);
      setCategories(cat.data || []);
    } catch { toast.error("Could not load skills"); }
  }, []);

  useEffect(() => { loadRows(); }, [loadRows]);

  async function addExtra() {
    const name = extraName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.post("/skills", { name, kind: "additional" });
      toast.success(`Added "${name}".`);
      setExtraName("");
      await Promise.all([loadRows(), load()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add skill");
    } finally {
      setSaving(false);
    }
  }

  async function removeSkill(id) {
    try {
      await api.patch(`/skills/${id}`, { is_archived: true });
      toast.success("Archived.");
      await Promise.all([loadRows(), load()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not archive");
    }
  }

  // Feb 2026 -- Skills step must not look empty when work categories
  // exist. Real assignment-skill rows (created via POST /skills with
  // kind=assignment) still take priority. For any work category that
  // does not yet have one, we show a derived read-only row so the
  // manager can see the linkage. "Link now" turns a derived row into
  // a real assignment skill via the existing POST /skills endpoint.
  const assignmentSkills = skills.filter((s) => s.kind === "assignment");
  const additionalSkills = skills.filter((s) => s.kind !== "assignment");
  const linkedCategoryIds = new Set(
    assignmentSkills.map((s) => s.source_category_id).filter(Boolean),
  );
  const derivedFromCategories = categories.filter(
    (c) => c.kind === "work" && c.is_active !== false
           && !linkedCategoryIds.has(c.category_id),
  );

  async function linkCategoryAsSkill(cat) {
    setLinkingCatId(cat.category_id);
    try {
      await api.post("/skills", {
        name: cat.name,
        kind: "assignment",
        source_category_id: cat.category_id,
      });
      toast.success(`Linked "${cat.name}" as an assignment skill.`);
      await Promise.all([loadRows(), load()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not link category");
    } finally {
      setLinkingCatId(null);
    }
  }

  const noWorkCategorySkills = assignmentSkills.length === 0
                                && derivedFromCategories.length === 0;

  return (
    <Card className="px-7 py-6 shadow-sm border-[#E4EAF4]" data-testid="setup-step-3-skills">
      <StepHeader n={3} done={completion[3]} icon={Lightbulb}
                  title="Set up employee skills"
                  subtitle="Skills are abilities your employees have. Some skills come from your work categories, and others can be extra skills such as Spanish, First Aid or Forklift." />
      <div className="mt-5 space-y-5">
        <p className="text-[12px] text-[#4B5773]"
           data-testid="setup-skills-intro">
          Work category skills come from your work categories.
          You can also add extra skills your employees may have.
        </p>

        <div>
          <label className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
            Work category skills (from your categories)
          </label>
          <div className="mt-2 border border-[#E4EAF4] rounded-lg overflow-hidden"
               data-testid="setup-work-category-skills-list">
            {noWorkCategorySkills && (
              <div className="text-[12px] text-[#94A3B8] px-3 py-4 text-center"
                   data-testid="setup-skills-assignment-empty">
                Add some categories in Step 2 first. Work category skills will show here.
              </div>
            )}
            {assignmentSkills.map((s) => (
              <div key={s.skill_id}
                   className="flex items-center justify-between px-3 py-2 border-b border-[#F1F5FB] last:border-b-0"
                   data-testid={`setup-assignment-skill-row-${s.skill_id}`}>
                <div className="flex items-center gap-2">
                  <Briefcase className="h-3.5 w-3.5 text-[#94A3B8]" />
                  <span className="text-[13px] text-[#0B1A36]">{s.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-emerald-700 uppercase tracking-widest font-medium">
                    Linked
                  </span>
                  <button
                    type="button"
                    onClick={() => removeSkill(s.skill_id)}
                    title="Keep this category for rota planning, but stop using it as an employee skill."
                    className="text-[11px] text-[#5775A6] hover:text-rose-600 underline-offset-2 hover:underline"
                    data-testid={`setup-assignment-skill-unlink-${s.skill_id}`}
                  >
                    Unlink
                  </button>
                </div>
              </div>
            ))}
            {derivedFromCategories.map((c) => (
              <div key={`cat_${c.category_id}`}
                   className="flex items-center justify-between px-3 py-2 border-b border-[#F1F5FB] last:border-b-0"
                   data-testid={`setup-derived-skill-row-${c.category_id}`}>
                <div className="flex items-center gap-2">
                  <Briefcase className="h-3.5 w-3.5 text-[#94A3B8]" />
                  <span className="text-[13px] text-[#0B1A36]">{c.name}</span>
                  <span className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">
                    From category
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => linkCategoryAsSkill(c)}
                  disabled={linkingCatId === c.category_id}
                  className="text-[11px] text-[#2563EB] hover:underline disabled:opacity-50"
                  data-testid={`setup-derived-skill-link-${c.category_id}`}
                >
                  {linkingCatId === c.category_id ? "Linking..." : "Link now"}
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
            Extra skills
          </label>
          <div className="flex gap-2 mt-2">
            <Input
              placeholder="e.g. Spanish, First Aid, Forklift, Keyholder"
              value={extraName}
              onChange={(e) => setExtraName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addExtra()}
              className="text-sm"
              data-testid="setup-skill-extra-input"
            />
            <Button onClick={addExtra} disabled={!extraName.trim() || saving}
                     data-testid="setup-skill-extra-add">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>
          <div className="mt-2 border border-[#E4EAF4] rounded-lg overflow-hidden">
            {additionalSkills.length === 0 ? (
              <div className="text-[12px] text-[#94A3B8] px-3 py-4 text-center"
                   data-testid="setup-skills-extras-empty">
                No extra skills yet. Add anything that is not already a work category.
              </div>
            ) : (
              additionalSkills.map((s) => (
                <div key={s.skill_id}
                     className="flex items-center justify-between px-3 py-2 border-b border-[#F1F5FB] last:border-b-0">
                  <span className="text-[13px] text-[#0B1A36]">{s.name}</span>
                  <button type="button" onClick={() => removeSkill(s.skill_id)}
                          className="text-[#94A3B8] hover:text-rose-600"
                          data-testid={`setup-skill-remove-${s.skill_id}`}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <StepFooter onBack={goBack} onNext={goNext}
                   nextLabel="Continue"
                   testIdPrefix="setup-step-3" />
    </Card>
  );
}


// ---------- Step 4 - Bank holidays --------------------------------

function Step4Holidays({ company, completion, load, goNext, goBack }) {
  const [holidays, setHolidays] = useState([]);
  const [rowsLoaded, setRowsLoaded] = useState(false);
  const [importing, setImporting] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [addingRow, setAddingRow] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [newName, setNewName] = useState("");
  const [newCountry, setNewCountry] = useState("");
  const year = new Date().getFullYear();
  // Feb 2026 -- memo the countries list so `company?.countries || []`
  // does not produce a fresh [] reference on every render, which
  // would invalidate loadRows and trigger an infinite useEffect loop
  // when the tenant has no countries yet.
  const countries = useMemo(() => company?.countries || [], [company]);

  const loadRows = useCallback(async () => {
    if (countries.length === 0) { setHolidays([]); setRowsLoaded(true); return; }
    try {
      const all = [];
      for (const cc of countries) {
        const r = await api.get("/bank-holidays", { params: { country_code: cc, year } });
        all.push(...(r.data || []));
      }
      all.sort((a, b) => (a.date < b.date ? -1 : 1));
      setHolidays(all);
    } catch { toast.error("Could not load holidays"); }
    finally { setRowsLoaded(true); }
  }, [countries, year]);

  useEffect(() => { loadRows(); }, [loadRows]);

  const merged = useMemo(() => {
    const byKey = {};
    for (const h of holidays) {
      const k = `${h.country_code}_${h.date}`;
      const isOverride = !!h.company_id;
      if (!byKey[k] || (isOverride && !byKey[k].company_id)) byKey[k] = h;
    }
    return Object.values(byKey).sort((a, b) => a.date < b.date ? -1 : 1);
  }, [holidays]);

  async function importAll() {
    if (countries.length === 0) {
      toast.error("Pick at least one country in Step 1 first.");
      return;
    }
    setImporting(true);
    let totalAdded = 0;
    let totalSkipped = 0;
    for (const cc of countries) {
      try {
        const r = await api.post("/bank-holidays/import",
                                  { country_code: cc, year });
        totalAdded += r.data?.added || 0;
        totalSkipped += r.data?.skipped || 0;
      } catch (err) {
        toast.error(`${cc}: ${err.response?.data?.detail || "import failed"}`);
      }
    }
    toast.success(`Imported ${totalAdded} bank holiday${totalAdded === 1 ? "" : "s"}. ${totalSkipped} already on file.`);
    await Promise.all([loadRows(), load()]);
    setImporting(false);
  }

  async function removeRow(id) {
    try {
      await api.delete(`/bank-holidays/${id}`);
      toast.success("Removed.");
      await Promise.all([loadRows(), load()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    }
  }

  async function toggleRow(h) {
    try {
      await api.patch(`/bank-holidays/${h.holiday_id}`, { is_active: !h.is_active });
      await loadRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    }
  }

  async function skipAll() {
    if (merged.length === 0) { goNext(); return; }
    setSkipping(true);
    let disabled = 0;
    for (const h of merged) {
      if (!h.is_active) continue;
      try {
        await api.patch(`/bank-holidays/${h.holiday_id}`, { is_active: false });
        disabled += 1;
      } catch (err) { /* non-fatal */ }
    }
    toast.success(`Bank holidays disabled for this workspace (${disabled}). You can manage them later from the Bank Holidays page.`);
    await Promise.all([loadRows(), load()]);
    setSkipping(false);
    goNext();
  }

  async function addCustom() {
    if (!newDate || !newName.trim() || !newCountry) {
      toast.error("Date, name and country are required.");
      return;
    }
    try {
      await api.post("/bank-holidays", {
        country_code: newCountry, date: newDate, name: newName.trim(),
      });
      toast.success("Custom holiday added.");
      setNewDate(""); setNewName(""); setNewCountry("");
      setAddingRow(false);
      await Promise.all([loadRows(), load()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add");
    }
  }

  const activeCount = merged.filter((h) => h.is_active).length;

  // Feb 2026 -- warn when multiple countries are assigned to the
  // tenant but bank holidays have not been imported for all of
  // them. Uses the same rows already loaded above; a country counts
  // as "has holidays" when at least one active row exists for it
  // in the current year. Read-only computation, no data changes.
  const countriesWithActiveHolidays = useMemo(() => {
    const s = new Set();
    for (const h of merged) {
      if (h.is_active && h.country_code) s.add(h.country_code);
    }
    return s;
  }, [merged]);
  const missingCountries = useMemo(
    () => countries.filter((cc) => !countriesWithActiveHolidays.has(cc)),
    [countries, countriesWithActiveHolidays],
  );

  return (
    <Card className="px-7 py-6 shadow-sm border-[#E4EAF4]" data-testid="setup-step-4-holidays">
      <StepHeader n={4} done={completion[4]} icon={CalendarIcon}
                  title="Set up bank holidays"
                  subtitle="We have suggested bank holidays based on your selected countries. You can add your own, disable any that do not apply, or keep the list as it is." />
      <div className="mt-5 space-y-4">
        {countries.length === 0 && (
          <div className="flex items-start gap-2 text-[12px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                data-testid="setup-holidays-needs-countries">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Pick at least one country in Step 1 before importing holidays.</span>
          </div>
        )}
        {/* Feb 2026 -- loading line while the holiday check runs so
            the missing-country warning never flashes before rows
            have loaded. */}
        {!rowsLoaded && countries.length > 0 && (
          <div className="flex items-center gap-2 text-[12px] text-[#4B5773]"
               data-testid="setup-holidays-checking">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Checking bank holidays for your countries...
          </div>
        )}
        {/* Feb 2026 -- multi-country warning. Only shows after the
            holiday rows have finished loading, when the tenant has
            more than one assigned country AND at least one of them
            has no active holidays imported for the current year. */}
        {rowsLoaded && countries.length > 1 && missingCountries.length > 0 && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-3"
                data-testid="setup-holidays-missing-warning">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-700" />
            <div className="flex-1 text-[12px] text-amber-900">
              <div className="font-semibold">Bank holidays missing for some countries</div>
              <div className="mt-0.5">
                You have added multiple countries, but holidays have not
                been imported for all of them. Import holidays for each
                country before continuing.
              </div>
              <div className="mt-1 text-[11px] text-amber-800">
                Missing:
                {" "}
                <span className="font-semibold"
                      data-testid="setup-holidays-missing-list">
                  {missingCountries.join(", ")}
                </span>
              </div>
            </div>
            <Button size="sm" onClick={importAll} disabled={importing}
                     data-testid="setup-holidays-missing-import-btn"
                     className="bg-amber-600 hover:bg-amber-700 text-white">
              {importing
                ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <CalendarIcon className="h-4 w-4 mr-1.5" />}
              Import missing holidays
            </Button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={importAll} disabled={importing || countries.length === 0}
                    data-testid="setup-holidays-import-btn">
            {importing
              ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              : <CalendarIcon className="h-4 w-4 mr-1.5" />}
            Import {year} holidays for {countries.length || 0} countr{countries.length === 1 ? "y" : "ies"}
          </Button>
          <Button variant="outline" onClick={() => setAddingRow((v) => !v)}
                    data-testid="setup-holidays-add-custom-btn">
            <Plus className="h-4 w-4 mr-1" /> Add custom
          </Button>
          <span className="text-[11px] text-[#94A3B8]">
            Existing holidays are skipped. Safe to re-run.
          </span>
        </div>

        {addingRow && (
          <div className="border border-[#E4EAF4] bg-[#F8FAFD] rounded-lg px-3 py-2 flex flex-wrap gap-2 items-end"
                data-testid="setup-holidays-add-form">
            <div>
              <label className="text-[10px] uppercase text-[#94A3B8] block mb-0.5">Country</label>
              <select className="h-8 rounded-md border border-[#CFDBEE] px-2 text-sm bg-white"
                        value={newCountry} onChange={(e) => setNewCountry(e.target.value)}
                        data-testid="setup-holidays-add-country">
                <option value="">Pick...</option>
                {countries.map((cc) => <option key={cc} value={cc}>{cc}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-[#94A3B8] block mb-0.5">Date</label>
              <input type="date" className="h-8 rounded-md border border-[#CFDBEE] px-2 text-sm"
                      value={newDate} onChange={(e) => setNewDate(e.target.value)}
                      data-testid="setup-holidays-add-date" />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="text-[10px] uppercase text-[#94A3B8] block mb-0.5">Name</label>
              <input type="text" className="h-8 w-full rounded-md border border-[#CFDBEE] px-2 text-sm"
                      value={newName} onChange={(e) => setNewName(e.target.value)}
                      placeholder="e.g. Company Anniversary"
                      data-testid="setup-holidays-add-name" />
            </div>
            <Button size="sm" onClick={addCustom} data-testid="setup-holidays-add-save">
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAddingRow(false)}>
              Cancel
            </Button>
          </div>
        )}

        <div className="border border-[#E4EAF4] rounded-lg overflow-hidden">
          {merged.length === 0 ? (
            <div className="text-[12px] text-[#94A3B8] px-3 py-4 text-center"
                  data-testid="setup-holidays-empty">
              No holidays yet. Click Import to fetch them automatically, or Add custom.
            </div>
          ) : (
            <table className="w-full text-[12px]">
              <thead className="bg-[#F1F5FB] text-[10px] uppercase tracking-widest text-[#5775A6]">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Date</th>
                  <th className="text-left px-3 py-2 font-semibold">Country</th>
                  <th className="text-left px-3 py-2 font-semibold">Name</th>
                  <th className="text-left px-3 py-2 font-semibold">Source</th>
                  <th className="text-right px-3 py-2 font-semibold w-20">Enabled</th>
                </tr>
              </thead>
              <tbody>
                {merged.map((h) => {
                  const isRealCustom = !!h.company_id && !h.overrides_holiday_id;
                  const toggleTitle = h.is_active
                    ? (isRealCustom ? "Disable this custom holiday" : "Disable for this workspace")
                    : "Enable for this workspace";
                  return (
                    <tr key={h.holiday_id}
                        className={`border-t border-[#F1F5FB] ${!h.is_active ? "opacity-60" : ""}`}
                        data-testid={`setup-holiday-row-${h.holiday_id}`}>
                      <td className="px-3 py-2 text-[#0B1A36] font-medium">{h.date}</td>
                      <td className="px-3 py-2 text-[#4B5773]">{h.country_code}</td>
                      <td className="px-3 py-2 text-[#0B1A36]">
                        {h.name}
                        {!h.is_active && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500"
                                data-testid={`setup-holiday-disabled-${h.date}`}>
                            Disabled for this workspace
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[#94A3B8] text-[10px] uppercase tracking-widest">
                        {h.company_id ? "company override" :
                          h.source === "library:holidays" ? "official library" : "global seed"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isRealCustom ? (
                          <div className="inline-flex items-center gap-1">
                            <button type="button"
                                    onClick={() => toggleRow(h)}
                                    title={toggleTitle}
                                    className={`h-6 w-11 rounded-full border transition ${h.is_active ? "bg-[#2563EB] border-[#2563EB]" : "bg-slate-200 border-slate-300"}`}
                                    data-testid={`setup-holiday-toggle-${h.date}`}>
                              <span className={`block h-4 w-4 rounded-full bg-white shadow transform transition ${h.is_active ? "translate-x-6" : "translate-x-1"}`} />
                            </button>
                            <button type="button"
                                    onClick={() => removeRow(h.holiday_id)}
                                    className="text-[#94A3B8] hover:text-rose-600 ml-1"
                                    title="Delete custom holiday"
                                    data-testid={`setup-holiday-remove-${h.holiday_id}`}>
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <button type="button"
                                  onClick={() => toggleRow(h)}
                                  title={toggleTitle}
                                  className={`h-6 w-11 rounded-full border transition ${h.is_active ? "bg-[#2563EB] border-[#2563EB]" : "bg-slate-200 border-slate-300"}`}
                                  data-testid={`setup-holiday-toggle-${h.date}`}>
                            <span className={`block h-4 w-4 rounded-full bg-white shadow transform transition ${h.is_active ? "translate-x-6" : "translate-x-1"}`} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="text-[11px] text-[#4B5773]"
                data-testid="setup-holidays-active-count">
            {activeCount} enabled for this workspace. {merged.length - activeCount} disabled.
          </div>
          <Button variant="ghost" size="sm" onClick={skipAll}
                  disabled={skipping}
                  data-testid="setup-holidays-skip-btn">
            {skipping ? "Disabling..." : "Skip for now (disable all)"}
          </Button>
        </div>
      </div>
      <StepFooter onBack={goBack} onNext={goNext}
                    nextLabel="Continue with defaults"
                    testIdPrefix="setup-step-4" />
    </Card>
  );
}


// ---------- Step 5 - Locations ------------------------------------

function Step5Locations({ completion, load, goNext, goBack }) {
  const [locations, setLocations] = useState([]);
  const [name, setName] = useState("");
  const [type, setType] = useState("office");
  const [saving, setSaving] = useState(false);

  const loadRows = useCallback(async () => {
    try {
      const r = await api.get("/locations");
      setLocations((r.data || []).filter((l) => !l.is_archived));
    } catch { toast.error("Could not load locations"); }
  }, []);
  useEffect(() => { loadRows(); }, [loadRows]);

  async function addLocation() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await api.post("/locations", { name: trimmed, location_type: type });
      toast.success(`Added "${trimmed}".`);
      setName("");
      await Promise.all([loadRows(), load()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add location");
    } finally { setSaving(false); }
  }

  async function archive(id) {
    try {
      await api.post(`/locations/${id}/archive`);
      toast.success("Archived.");
      await Promise.all([loadRows(), load()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not archive");
    }
  }

  const TYPES = [
    { v: "office",      l: "Office" },
    { v: "wfh",         l: "Work from home" },
    { v: "hybrid",      l: "Hybrid" },
    { v: "depot",       l: "Depot" },
    { v: "remote",      l: "Remote" },
    { v: "client_site", l: "Client site" },
    { v: "other",       l: "Other" },
  ];

  return (
    <Card className="px-7 py-6 shadow-sm border-[#E4EAF4]" data-testid="setup-step-5-locations">
      <StepHeader n={5} done={completion[5]} icon={MapPin}
                  title="Add work locations"
                  subtitle="Locations are the places where employees may work, such as offices, home, shops, depots, client sites or remote work. Add them now if you want shifts to show where employees need to be." />
      <div className="mt-5 space-y-4">
        <div className="rounded-lg border border-[#E4EAF4] bg-[#F8FAFD] px-4 py-3 text-[12px] text-[#4B5773]"
             data-testid="setup-locations-skip-hint">
          You can skip this step if your team works from one place or does not need location tracking.
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-2">
          <Input placeholder="e.g. London HQ, Manchester depot"
                 value={name} onChange={(e) => setName(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && addLocation()}
                 className="text-sm"
                 data-testid="setup-location-name-input" />
          <select value={type} onChange={(e) => setType(e.target.value)}
                  className="text-sm px-2.5 py-1.5 rounded-md border border-[#E4EAF4] bg-white"
                  data-testid="setup-location-type-select">
            {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          <Button onClick={addLocation} disabled={!name.trim() || saving}
                   data-testid="setup-location-add">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>

        <div className="border border-[#E4EAF4] rounded-lg overflow-hidden">
          {locations.length === 0 ? (
            <div className="text-[12px] text-[#94A3B8] px-3 py-4 text-center"
                 data-testid="setup-locations-empty">
              No locations yet. Add your first above.
            </div>
          ) : (
            locations.map((l) => (
              <div key={l.location_id}
                   className="flex items-center justify-between px-3 py-2 border-b border-[#F1F5FB] last:border-b-0"
                   data-testid={`setup-location-row-${l.location_id}`}>
                <div className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-[#94A3B8]" />
                  <span className="text-[13px] text-[#0B1A36]">{l.name}</span>
                  <span className="text-[10px] uppercase tracking-widest text-[#94A3B8]">
                    {(TYPES.find((t) => t.v === l.location_type) || {}).l || l.location_type || ""}
                  </span>
                </div>
                <button type="button" onClick={() => archive(l.location_id)}
                        className="text-[#94A3B8] hover:text-rose-600"
                        data-testid={`setup-location-archive-${l.location_id}`}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
      <StepFooter onBack={goBack} onNext={goNext}
                   nextLabel={locations.length > 0 ? "Continue" : "Skip for now"}
                   testIdPrefix="setup-step-5" />
    </Card>
  );
}


// ---------- Step 6 - Shift templates (NEW) ------------------------

function Step6ShiftTemplates({ completion, goNext, goBack, navigate, templatesCount }) {
  return (
    <Card className="px-7 py-6 shadow-sm border-[#E4EAF4]" data-testid="setup-step-6-shift-templates">
      <StepHeader n={6} done={completion[6]} icon={Layers}
                  title="Review your shift templates"
                  subtitle="Shift templates are reusable shift shapes with work rows, breaks and times. Use them in roster cells, Bulk apply shift, Rota Style cells and Bulk apply pattern." />
      <div className="mt-5 space-y-4">
        <div className="rounded-lg border border-[#E4EAF4] bg-[#F8FAFD] px-4 py-3 text-[12px] text-[#4B5773]"
             data-testid="setup-shift-templates-hint">
          You have {templatesCount} shift template{templatesCount === 1 ? "" : "s"} ready to use.
          You can use the default templates now and change them later.
        </div>

        <div className="rounded-lg border border-[#E4EAF4] bg-white px-4 py-3">
          <p className="text-[12px] text-[#4B5773]">
            Templates save managers time because they do not need to
            rebuild common work and break patterns every time.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline"
                    onClick={() => navigate("/app/shift-templates")}
                    data-testid="setup-shift-templates-open">
              Open Shift templates
              <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      </div>
      <StepFooter onBack={goBack} onNext={goNext}
                  nextLabel={templatesCount > 0 ? "Continue" : "Skip for now"}
                  testIdPrefix="setup-step-6" />
    </Card>
  );
}


// ---------- Step 7 - Teams (no Location, Advanced parent) --------

function Step7Teams({ completion, load, goNext, goBack }) {
  const [teams, setTeams] = useState([]);
  const [name, setName] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [parentTeamId, setParentTeamId] = useState("");
  const [saving, setSaving] = useState(false);

  const loadRows = useCallback(async () => {
    try {
      const t = await api.get("/teams");
      setTeams(t.data || []);
    } catch { toast.error("Could not load teams"); }
  }, []);
  useEffect(() => { loadRows(); }, [loadRows]);

  async function addTeam() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const payload = { name: trimmed };
      if (parentTeamId) payload.parent_team_id = parentTeamId;
      await api.post("/teams", payload);
      toast.success(`Added "${trimmed}".`);
      setName("");
      setParentTeamId("");
      setShowAdvanced(false);
      await Promise.all([loadRows(), load()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add team");
    } finally { setSaving(false); }
  }

  async function removeTeam(id) {
    try {
      await api.delete(`/teams/${id}`);
      toast.success("Removed.");
      await Promise.all([loadRows(), load()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove team");
    }
  }

  return (
    <Card className="px-7 py-6 shadow-sm border-[#E4EAF4]" data-testid="setup-step-7-teams">
      <StepHeader n={7} done={completion[7]} icon={Users}
                  title="Create your teams"
                  subtitle="Teams are groups of employees who share the same rota or manager, such as Front Desk, Warehouse, Night Team or UK Helpdesk." />
      <div className="mt-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
          <Input placeholder="e.g. UK Helpdesk, Warehouse Day Shift"
                 value={name} onChange={(e) => setName(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && addTeam()}
                 className="text-sm"
                 data-testid="setup-team-name-input" />
          <Button onClick={addTeam} disabled={!name.trim() || saving}
                   data-testid="setup-team-add">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>

        <div>
          <button type="button"
                  onClick={() => setShowAdvanced((v) => !v)}
                  className="text-[12px] text-[#2563EB] hover:underline inline-flex items-center gap-1"
                  data-testid="setup-team-advanced-toggle">
            {showAdvanced ? "Hide advanced options" : "Advanced options"}
            <ChevronDown className={`h-3 w-3 transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
          </button>
          {showAdvanced && (
            <div className="mt-2 border border-[#E4EAF4] bg-[#F8FAFD] rounded-lg px-3 py-3"
                 data-testid="setup-team-advanced-panel">
              <label className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
                Parent team (optional)
              </label>
              <select value={parentTeamId} onChange={(e) => setParentTeamId(e.target.value)}
                      className="mt-1 w-full text-sm px-2.5 py-1.5 rounded-md border border-[#CFDBEE] bg-white"
                      data-testid="setup-team-parent-select">
                <option value="">No parent team</option>
                {teams.map((t) => (
                  <option key={t.team_id} value={t.team_id}>{t.name}</option>
                ))}
              </select>
              <p className="text-[11px] text-[#4B5773] mt-1">
                Use this only if you want to nest a sub-team under an existing team.
              </p>
            </div>
          )}
        </div>

        <div className="border border-[#E4EAF4] rounded-lg overflow-hidden">
          {teams.length === 0 ? (
            <div className="text-[12px] text-[#94A3B8] px-3 py-4 text-center"
                 data-testid="setup-teams-empty">
              No teams yet. Add your first above.
            </div>
          ) : (
            teams.map((t) => (
              <div key={t.team_id}
                   className="flex items-center justify-between px-3 py-2 border-b border-[#F1F5FB] last:border-b-0"
                   data-testid={`setup-team-row-${t.team_id}`}>
                <div className="flex items-center gap-2">
                  <Users className="h-3.5 w-3.5 text-[#94A3B8]" />
                  <span className="text-[13px] text-[#0B1A36]">{t.name}</span>
                </div>
                <button type="button" onClick={() => removeTeam(t.team_id)}
                        className="text-[#94A3B8] hover:text-rose-600"
                        data-testid={`setup-team-remove-${t.team_id}`}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
      <StepFooter onBack={goBack} onNext={goNext}
                   nextLabel={teams.length > 0 ? "Continue" : "Skip for now"}
                   nextDisabled={teams.length === 0}
                   testIdPrefix="setup-step-7" />
    </Card>
  );
}


// ---------- Step 8 - Working patterns ----------------------------

// Simple presets created directly inside Account setup. Each timed
// preset creates a real 1-week scheduled_cycle pattern through the
// existing POST /patterns endpoint, so it appears on the full
// Working Patterns page immediately. "flexible_none" creates
// nothing; it records an explicit "no fixed patterns" choice in the
// localStorage reviewed store (no new backend field).
const STEP8_PRESETS = [
  { key: "setup_standard_weekdays", label: "Standard weekdays",
    desc: "Monday to Friday, 09:00 to 17:00",
    days: [0, 1, 2, 3, 4], start: "09:00", end: "17:00" },
  { key: "setup_part_time_mornings", label: "Part-time mornings",
    desc: "Monday to Friday, 09:00 to 13:00",
    days: [0, 1, 2, 3, 4], start: "09:00", end: "13:00" },
  { key: "setup_part_time_afternoons", label: "Part-time afternoons",
    desc: "Monday to Friday, 13:00 to 17:00",
    days: [0, 1, 2, 3, 4], start: "13:00", end: "17:00" },
  { key: "setup_weekend_cover", label: "Weekend cover",
    desc: "Saturday and Sunday, 09:00 to 17:00",
    days: [5, 6], start: "09:00", end: "17:00" },
  { key: "flexible_none", label: "Flexible or no fixed pattern",
    desc: "Use this if your team changes week by week." },
];

const STEP8_WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function hhmmToMinutes(hhmm) {
  const [h, m] = (hhmm || "0:0").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

function Step8Patterns({ completion, goNext, goBack, navigate, markReviewed }) {
  const [patterns, setPatterns] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/patterns").catch(() => ({ data: [] }));
      setPatterns(Array.isArray(r.data) ? r.data : []);
    } finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const hasPatterns = patterns.length > 0;
  const flexibleChosen = selectedKey === "flexible_none";
  const canContinue = hasPatterns || flexibleChosen;

  function selectPreset(p) {
    setSelectedKey(p.key);
    if (p.key === "flexible_none") { setForm(null); return; }
    setForm({ name: p.label, days: [...p.days], start: p.start, end: p.end, note: "" });
  }

  function toggleDay(wd) {
    setForm((f) => {
      const has = f.days.includes(wd);
      const days = has ? f.days.filter((d) => d !== wd)
                       : [...f.days, wd].sort((a, b) => a - b);
      return { ...f, days };
    });
  }

  const perDayMinutes = form
    ? Math.max(0, hhmmToMinutes(form.end) - hhmmToMinutes(form.start))
    : 0;
  const canSave = !!form && form.name.trim().length > 0
    && form.days.length > 0 && perDayMinutes > 0 && !saving;

  async function createPattern() {
    if (!canSave) return;
    setSaving(true);
    try {
      await api.post("/patterns", {
        name: form.name.trim(),
        description: form.note.trim() || null,
        type: "scheduled_cycle",
        scheduled_cycle: {
          cycle_length_weeks: 1,
          cycle_start_date: todayIsoMonday(),
          weeks: buildScheduledCycleWeeks(
            1, form.days, perDayMinutes * form.days.length, form.start),
        },
        flexible_weekly: null,
        zero_hours: null,
        is_active: true,
        is_archived: false,
        rsa_auto_generate: false,
        setup_template_key: selectedKey,
      });
      toast.success("Working pattern saved.");
      setSelectedKey(null);
      setForm(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the pattern");
    } finally { setSaving(false); }
  }

  function continueStep() {
    if (typeof markReviewed === "function") markReviewed(8);
    goNext();
  }

  return (
    <Card className="px-7 py-6 shadow-sm border-[#E4EAF4]" data-testid="setup-step-8-patterns">
      <StepHeader n={8} done={completion[8]} icon={Clock}
                  title="Set working patterns"
                  subtitle="Working patterns help you plan regular weekly schedules. Choose a simple starting pattern now, or continue without one and build your first roster manually." />
      <p className="mt-3 text-[12px] text-slate-500 rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
         data-testid="setup-wp-helper-copy">
        Use this for the employee&apos;s usual weekly hours and first-week pattern. You can set rotating shifts later in Roster or Rota Style.
      </p>
      <div className="mt-5 space-y-4">
        {loaded && hasPatterns && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3"
               data-testid="setup-patterns-existing">
            <p className="text-[12px] font-semibold text-emerald-900 mb-1.5">
              Your working patterns ({patterns.length})
            </p>
            <ul className="text-[12px] text-emerald-900 space-y-1">
              {patterns.slice(0, 6).map((p) => (
                <li key={p.pattern_id} className="flex items-center gap-1.5"
                    data-testid={`setup-patterns-row-${p.pattern_id}`}>
                  <Check className="h-3 w-3 shrink-0" /> {p.name}
                </li>
              ))}
              {patterns.length > 6 && (
                <li className="text-emerald-800">and {patterns.length - 6} more</li>
              )}
            </ul>
          </div>
        )}

        <div>
          <p className="text-[12px] font-semibold text-[#0B1A36] mb-2">
            {hasPatterns ? "Add another simple pattern" : "Choose a simple starting pattern"}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {STEP8_PRESETS.map((p) => {
              const isSel = selectedKey === p.key;
              return (
                <button key={p.key} type="button" onClick={() => selectPreset(p)}
                        data-testid={`setup-patterns-preset-${p.key}`}
                        className={[
                          "text-left rounded-lg border px-3.5 py-3 transition-colors",
                          isSel
                            ? "border-[#2563EB] bg-[#EFF3FB]"
                            : "border-[#E4EAF4] bg-white hover:border-[#B9C8E4]",
                        ].join(" ")}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[13px] font-semibold text-[#0B1A36]">{p.label}</span>
                    {isSel && <Check className="h-4 w-4 text-[#2563EB] shrink-0" />}
                  </div>
                  <p className="text-[11px] text-[#4B5773] mt-0.5">{p.desc}</p>
                </button>
              );
            })}
          </div>
        </div>

        {form && !flexibleChosen && (
          <div className="rounded-lg border border-[#E4EAF4] bg-[#F8FAFD] px-4 py-3 space-y-3"
               data-testid="setup-patterns-editor">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase text-[#94A3B8] block mb-0.5">Pattern name</label>
                <Input value={form.name}
                       onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                       className="h-8 text-sm bg-white"
                       data-testid="setup-patterns-name-input" />
              </div>
              <div className="flex gap-3">
                <div>
                  <label className="text-[10px] uppercase text-[#94A3B8] block mb-0.5">Start</label>
                  <Input type="time" value={form.start}
                         onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
                         className="h-8 text-sm bg-white w-[110px]"
                         data-testid="setup-patterns-start-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-[#94A3B8] block mb-0.5">End</label>
                  <Input type="time" value={form.end}
                         onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
                         className="h-8 text-sm bg-white w-[110px]"
                         data-testid="setup-patterns-end-input" />
                </div>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase text-[#94A3B8] block mb-1">Working days</label>
              <div className="flex gap-1.5 flex-wrap">
                {STEP8_WEEKDAYS.map((d, i) => {
                  const on = form.days.includes(i);
                  return (
                    <button key={d} type="button" onClick={() => toggleDay(i)}
                            data-testid={`setup-patterns-day-${d.toLowerCase()}`}
                            className={[
                              "px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors",
                              on
                                ? "bg-[#2563EB] border-[#2563EB] text-white"
                                : "bg-white border-[#CFDBEE] text-[#4B5773]",
                            ].join(" ")}>
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase text-[#94A3B8] block mb-0.5">Note (optional)</label>
              <Input value={form.note}
                     onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                     placeholder="For example: main office hours"
                     className="h-8 text-sm bg-white"
                     data-testid="setup-patterns-note-input" />
            </div>
            {perDayMinutes <= 0 && (
              <p className="text-[11px] text-amber-700" data-testid="setup-patterns-time-error">
                End time must be after the start time.
              </p>
            )}
            <div className="flex items-center gap-3 flex-wrap">
              <Button size="sm" onClick={createPattern} disabled={!canSave}
                      data-testid="setup-patterns-use-btn">
                {saving
                  ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  : <Plus className="h-4 w-4 mr-1" />}
                Use selected pattern
              </Button>
              <span className="text-[11px] text-[#94A3B8]">
                Saves a real working pattern you can assign to employees later.
              </span>
            </div>
          </div>
        )}

        {flexibleChosen && (
          <div className="rounded-lg border border-[#E4EAF4] bg-[#F8FAFD] px-4 py-3 text-[12px] text-[#4B5773]"
               data-testid="setup-patterns-flexible-note">
            No problem. You can build each week directly in the roster and add
            working patterns later at any time.
          </div>
        )}

        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Button size="sm" variant="outline"
                  onClick={() => navigate("/app/working-patterns")}
                  data-testid="setup-patterns-advanced">
            Advanced settings
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
          {loaded && !canContinue && (
            <p className="text-[11px] text-[#94A3B8]" data-testid="setup-patterns-choice-hint">
              Pick a starting pattern, or choose Flexible or no fixed pattern to continue.
            </p>
          )}
        </div>
      </div>
      <StepFooter onBack={goBack} onNext={continueStep}
                  nextDisabled={!canContinue}
                  nextLabel={flexibleChosen && !hasPatterns
                    ? "Continue without fixed patterns" : "Continue"}
                  testIdPrefix="setup-step-8" />
    </Card>
  );
}


// ---------- Step 9 - Employees and access ------------------------

function Step9Employees({ completion, goNext, goBack, navigate }) {
  const [employees, setEmployees] = useState([]);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/employees").catch(() => ({ data: [] }));
      setEmployees(r.data || []);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const scheduableCount = employees.filter(
    (e) => e.employment_status === "active" && e.include_in_roster !== false,
  ).length;

  return (
    <Card className="px-7 py-6 shadow-sm border-[#E4EAF4]" data-testid="setup-step-9-employees">
      <StepHeader n={9} done={completion[9]} icon={UserPlus}
                  title="Add employees and choose access"
                  subtitle="Add the people who will appear in your rosters. You can also choose what access each person should have." />
      <div className="mt-5 space-y-4">
        <div className="rounded-lg border border-[#E4EAF4] bg-white px-4 py-3">
          <p className="text-[12px] font-semibold text-[#0B1A36] mb-2">Access levels</p>
          <ul className="text-[12px] text-[#4B5773] space-y-2"
              data-testid="setup-employees-access-explainer">
            <li>
              <span className="font-semibold text-[#0B1A36]">Workspace Admin.</span>
              {" "}Full access to the whole workspace, including setup, teams, rosters and admin settings.
            </li>
            <li>
              <span className="font-semibold text-[#0B1A36]">Team Admin.</span>
              {" "}Can manage selected teams, depending on the permissions you give them.
            </li>
            <li>
              <span className="font-semibold text-[#0B1A36]">Team Admin with additional teams.</span>
              {" "}Choose a main team and any extra teams this admin can help manage.
            </li>
            <li>
              <span className="font-semibold text-[#0B1A36]">Employee.</span>
              {" "}Can view assigned shifts, team rota information, leave and swaps where available.
            </li>
          </ul>
        </div>

        <div className="rounded-lg border border-[#E4EAF4] bg-[#F8FAFD] px-4 py-3 text-[12px] text-[#4B5773]"
             data-testid="setup-employees-status">
          {scheduableCount > 0 ? (
            <>You have {scheduableCount} roster employee{scheduableCount === 1 ? "" : "s"} added.</>
          ) : (
            <>No roster employees yet. Open Employees to add at least one so you can build a roster.</>
          )}
        </div>

        <div>
          <Button size="sm" variant="outline"
                  onClick={() => navigate("/app/employees?from=setup")}
                  data-testid="setup-employees-open">
            Open Employees
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </div>
      <StepFooter onBack={goBack} onNext={goNext}
                  nextLabel={scheduableCount > 0 ? "Continue" : "Skip for now"}
                  nextDisabled={scheduableCount === 0}
                  testIdPrefix="setup-step-9" />
    </Card>
  );
}


// ---------- Step 10 - Create first roster + hand-off card --------

function Step10FirstRoster({ completion, goBack, navigate,
                              accountSetupComplete, billingMe }) {
  const isDone = !!completion[10];

  return (
    <Card className="px-7 py-6 shadow-sm border-[#E4EAF4]" data-testid="setup-step-10-first-roster">
      <StepHeader n={10} done={isDone} icon={Sparkles}
                  title="Create your first roster"
                  subtitle="Add shifts for the current week or next week using roster cells or Bulk apply shift. You can use shift templates to add work and breaks faster." />
      <div className="mt-5 space-y-4">
        <div className="rounded-lg border border-[#E4EAF4] bg-white px-4 py-3">
          <p className="text-[12px] text-[#4B5773]">
            When the roster is ready, publish it so employees can see their shifts.
            Publishing can also send roster emails to employees.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline"
                    onClick={() => navigate("/app/roster")}
                    data-testid="setup-first-roster-open">
              Open Roster
              <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
          <p className="text-[11px] text-[#4B5773] mt-3"
             data-testid="setup-first-roster-completion-hint">
            This step completes when at least one shift exists in the current week or next week.
            Publishing is not required to complete Account setup.
          </p>
        </div>

        {isDone && accountSetupComplete && (
          <HandOffCard billingMe={billingMe} navigate={navigate} />
        )}
      </div>
      <StepFooter onBack={goBack} onNext={null}
                  testIdPrefix="setup-step-10" />
    </Card>
  );
}


function HandOffCard({ billingMe, navigate }) {
  const growthPlus = hasGrowthAccess(billingMe);
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-5 py-4"
         data-testid="setup-handoff-card">
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 rounded-full bg-white grid place-items-center border border-emerald-200 shrink-0">
          <Check className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="flex-1">
          <p className="text-[13px] font-semibold text-[#0B1A36]"
             data-testid="setup-handoff-title">
            Next: Rota Style setup
          </p>
          {growthPlus ? (
            <>
              <p className="text-[12px] text-[#4B5773] mt-1"
                 data-testid="setup-handoff-body-growth">
                Your account setup is complete. You can set up advanced planning tools next.
              </p>
              <div className="mt-3">
                <Button size="sm" variant="outline"
                        onClick={() => navigate("/app/rota-style")}
                        data-testid="setup-handoff-open-rota-style">
                  Open Rota Style
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </>
          ) : (
            <p className="text-[12px] text-[#4B5773] mt-1"
               data-testid="setup-handoff-body-starter">
              Rota Style setup is available on Growth and above. You can still build and publish rosters manually from the Roster page.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------- Shared step header + footer + side cards -------------

function StepHeader({ n, done, icon: Icon, title, subtitle }) {
  return (
    <header className="flex gap-4">
      <div className="w-10 h-10 rounded-full bg-[#EFF3FB] flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-[#2563EB]" />
      </div>
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-widest text-[#5775A6] font-semibold">
          Step {n} of 10{done ? " . done" : ""}
        </p>
        <h2 className="text-[19px] font-semibold text-[#0B1A36] leading-tight mt-0.5">
          {title}
        </h2>
        {subtitle && <p className="text-[12px] text-[#4B5773] mt-1 leading-relaxed">{subtitle}</p>}
      </div>
      {done && (
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1 h-fit"
              data-testid="setup-step-header-done-chip">
          <Check className="h-3 w-3" /> Complete
        </span>
      )}
    </header>
  );
}


function StepFooter({ onBack, onNext, nextLabel, nextDisabled, saving, testIdPrefix }) {
  return (
    <div className="flex items-center gap-3 mt-6 pt-5 border-t border-[#F1F5FB]">
      {onBack && (
        <Button variant="outline" onClick={onBack} disabled={saving}
                 data-testid={`${testIdPrefix}-back`}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
      )}
      <div className="flex-1" />
      {onNext && (
        <Button onClick={onNext} disabled={saving || nextDisabled}
                 className="bg-[#2563EB] hover:bg-[#1D4FD6] text-white"
                 data-testid={`${testIdPrefix}-next`}>
          {saving
            ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            : null}
          {nextLabel || "Continue"}
          {!saving && <ArrowRight className="h-4 w-4 ml-1" />}
        </Button>
      )}
    </div>
  );
}


function SideCardWhy({ step }) {
  return (
    <Card className="px-5 py-5 shadow-sm border-[#E4EAF4]" data-testid="setup-side-why">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-full bg-emerald-50 flex items-center justify-center">
          <Lightbulb className="h-3.5 w-3.5 text-emerald-700" />
        </div>
        <p className="text-[13px] font-semibold text-[#0B1A36]">Why this matters</p>
      </div>
      <p className="text-[12px] text-[#4B5773] leading-relaxed">
        {step.why}
      </p>
    </Card>
  );
}


function SideCardWhatsNext({ currentStep, completion, onSelect }) {
  const upcoming = STEP_META.filter((s) => s.n > currentStep).slice(0, 5);
  if (upcoming.length === 0) return null;
  return (
    <Card className="px-5 py-5 shadow-sm border-[#E4EAF4]" data-testid="setup-side-next">
      <p className="text-[13px] font-semibold text-[#0B1A36] mb-3">
        What&apos;s next
      </p>
      <ul className="space-y-2">
        {upcoming.map((s) => (
          <li key={s.n}>
            <button
              type="button"
              onClick={() => onSelect(s.n)}
              className="w-full flex items-center gap-2.5 text-[12px] text-[#4B5773] hover:text-[#0B1A36] group"
              data-testid={`setup-next-step-${s.n}`}
            >
              <span className={[
                "w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold border",
                completion[s.n]
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                  : "bg-white border-[#E4EAF4] text-[#94A3B8] group-hover:border-[#2563EB]/40",
              ].join(" ")}>
                {completion[s.n] ? <Check className="h-3 w-3" /> : s.n}
              </span>
              <span className="truncate">{s.title}</span>
              <ChevronRight className="h-3.5 w-3.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}


function GlanceCard({ icon: Icon, value, label, hint, showBar, progressPct }) {
  return (
    <Card className="px-4 py-4 shadow-sm border-[#E4EAF4]">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#EFF3FB] flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-[#2563EB]" />
        </div>
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-widest text-[#5775A6] font-semibold">
            {label}
          </p>
          <p className="text-[22px] font-semibold text-[#0B1A36] leading-none mt-1">
            {value}
          </p>
          <p className="text-[11px] text-[#4B5773] mt-1">{hint}</p>
          {showBar && (
            <div className="h-1.5 bg-[#E4EAF4] rounded-full mt-2 overflow-hidden">
              <div className="h-full bg-emerald-500 transition-[width] duration-500"
                   style={{ width: `${progressPct}%` }} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
